import { Injectable } from '@angular/core';
import { DpChartColor, DpLineChart, DpLineChartOptions, DpLineXAxisParameters, DpLineYAxisParameters, DpPieChart, DpPieChartOptions, DpThemeType } from '@docpath/webcomponents';
import { LineSeriesData, LineSeriesDataParameters, MarkLineData, MarkLineOptions } from '@docpath/webcomponents/shared/chart/models/line-chart.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { AlertRule } from '../../../configuration/alert-rules/models/alert-rule';
import { AssignedRule } from '../../models/assigned-rule';
import { MonitoringResult, SearchKeyType } from '../../models/monitoring';
import { ReferenceValues } from '../../models/reference-values.';
import { MonitoringConstants } from '../../monitoring-constants';
import { MonitoringHttpService } from '../../services/monitoring-http.service';

@Injectable({
    providedIn: 'root'
})
export class MonitoringChartService {

    private readonly i18nPrefix = 'monitoring.chart';
    private readonly i18nPrefixDisk = `${this.i18nPrefix}.disk`;

    constructor(
        private readonly monitoringHttpService: MonitoringHttpService,
        private readonly translateService: TranslateService
    ) { }

    loadCpuFilterCardSubtitle(cpu: number, cores: number): string {
        let subtitleTranslated = '';
        if (cpu && cores) {
            const parameterObjects: { [key: string]: string; } = {};
            parameterObjects[`param${0}`] = cpu.toString();
            parameterObjects[`param${1}`] = cores.toString();

            this.translateService.get(`${this.i18nPrefix}.cpu.subtitle`, parameterObjects).subscribe(
                (subtitle: string) => {
                    subtitleTranslated = subtitle;
                }
            );
        }
        return subtitleTranslated;
    }

    loadRamFilterCardSubtitle(ram: number, java: number, filterSelected: string): string {
        let subtitleTranslated = '';
        if (ram && java) {
            const parameterObjects: { [key: string]: string; } = {};
            parameterObjects[`param${0}`] = (filterSelected === MonitoringConstants.CHART_RAM_SYSTEM) ? ram.toString() : java.toString();

            const subtitleKey = (filterSelected === MonitoringConstants.CHART_RAM_SYSTEM) ? `${this.i18nPrefix}.ram.subtitle-system` : `${this.i18nPrefix}.ram.subtitle-java`;
            this.translateService.get(subtitleKey, parameterObjects).subscribe(
                (subtitle: string) => {
                    subtitleTranslated = subtitle;
                }
            );
        }
        return subtitleTranslated;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loadPieSerieTranslatedText(customOptions: any): void {
        if (customOptions) {
            this.translateService.get(`${this.i18nPrefixDisk}.available`).subscribe((translatedText: string) => {
                customOptions[0].style.text = translatedText;
            });
            this.translateService.get(`${this.i18nPrefixDisk}.used`).subscribe((translatedText: string) => {
                customOptions[2].style.text = translatedText;
            });
            this.translateService.get(`${this.i18nPrefixDisk}.total`).subscribe((translatedText: string) => {
                customOptions[4].style.text = translatedText;
            });
        }
    }

    getReferenceValues(dgeId: number): Observable<ReferenceValues> {
        return this.monitoringHttpService.getReferenceValues(dgeId);
    }

    getRulesAssignment(dgeId: number): Observable<AssignedRule[]> {
        return this.monitoringHttpService.getAssignedAlertRules(dgeId);
    }

    initLineChart(assignedAlertRules: AlertRule[], type: SearchKeyType, monitoringResults: MonitoringResult[], lineColor: string, itemColor: string): DpLineChart {
        const xAxisParameters: DpLineXAxisParameters = this.getLineXAxisOptions(monitoringResults.length);
        const yAxisParameters: DpLineYAxisParameters = {
            max: 1, interval: 0.25, axisLabelFormatter: (value: number) => `${(value * 100).toFixed(0)}%`
        };

        const monitoring: LineSeriesData[] = monitoringResults.map(result => ({
            name: result.xAxis,
            value: [result.xAxis, result.yAxis]
        }));

        const seriesData: LineSeriesDataParameters[] = [
            { name: '', value: monitoring, lineStyle: { width: 3, color: lineColor }, itemStyle: { color: itemColor }, symbolSize: 6, markLine: this.getMarkLineOptions(assignedAlertRules, type) }
        ];

        const lineChartOptions = new DpLineChartOptions({
            xAxisParameters,
            yAxisParameters,
            seriesData,
            showLegend: false,
            tooltipFormatter: this.getLineTooltipFormatter
        });

        return new DpLineChart({ containerHeight: '350px', options: lineChartOptions });
    }

    initPieChart(monitoringResult: MonitoringResult[], referenceValues: ReferenceValues, theme: DpThemeType): DpPieChart {
        const totalDisk = this.roundToNearestEven(referenceValues.disk / 1000);

        const usedResult = monitoringResult.find(result => result.xAxis === MonitoringConstants.USED);
        const freeResult = monitoringResult.find(result => result.xAxis === MonitoringConstants.FREE);

        const usedPercentage = usedResult ? Math.round(usedResult.yAxis * 100) : 0;
        const freePercentage = freeResult ? Math.round(freeResult.yAxis * 100) : 0;

        const usedDisk = totalDisk ? Number.parseFloat((usedPercentage / 100 * totalDisk).toFixed(2)) : Number.parseFloat((usedPercentage).toFixed(2));
        const availableDisk = totalDisk ? Number.parseFloat((freePercentage / 100 * totalDisk).toFixed(2)) : Number.parseFloat((freePercentage).toFixed(2));

        return new DpPieChart({
            containerHeight: '350px',
            options: new DpPieChartOptions({
                seriesData: [
                    {
                        value: availableDisk ?? freePercentage, name: `${this.i18nPrefixDisk}.available`, color: DpChartColor.Green
                    },
                    {
                        value: usedDisk ?? usedPercentage, name: `${this.i18nPrefixDisk}.used`, color: DpChartColor.Red
                    }
                ].filter(data => data.value > 0),
                radius: ['20%', '60%'],
                center: ['50%', '35%'],
                tooltipFormatter: '{b}, {c} GBs',
                showLegend: false,
                customOptions: referenceValues.disk ? this.getPieCustomOptions(usedDisk, availableDisk, totalDisk, theme) : undefined
            })
        });
    }

    changePieChartTheme(pieChart: DpPieChart | undefined, theme: DpThemeType): void {
        if (pieChart?.echartOptions && Array.isArray(pieChart.echartOptions.graphic)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pieChart.echartOptions.graphic.forEach((object: any, index) => {
                object.style.fill = this.getColorText(index, theme);
            });
        }
    }

    private getMarkLineOptions(assignedAlertRules: AlertRule[], type: SearchKeyType): MarkLineOptions | undefined {
        const markLineData: MarkLineData[] = [];

        if (assignedAlertRules.length > 0) {
            assignedAlertRules.forEach(rule => {
                if (rule.measured_resource.toLowerCase() === type as string) {
                    markLineData.push({
                        yAxis: rule.percent_limit / 100,
                        name: rule.rule_name,
                        lineStyle: { color: rule.color, width: 3 }
                    });
                }
            });
            return { symbol: 'none', silent: true, label: { show: false }, data: markLineData };
        }
        return undefined;
    }

    private getLineXAxisOptions(monitoringResultsLength: number): DpLineXAxisParameters {
        let xAxisParameters: DpLineXAxisParameters = {};

        const axisLabelFormatter = (value: number) => {
            const totalDataPoints = monitoringResultsLength;
            const date = new Date(value);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
            const formattedTime = `${hours}:${formattedMinutes}`;

            if (totalDataPoints > 30) {
                return this.formatTimeForTotalDataPointsGreaterThan30(minutes, date, formattedTime);
            }
            if (totalDataPoints >= 16 && totalDataPoints <= 30) {
                return this.formatTimeForTotalDataPointsBetween16And30(minutes, date, formattedTime);
            }
            if (totalDataPoints >= 11 && totalDataPoints <= 15) {
                return this.formatTimeForTotalDataPointsBetween11And15(minutes, date, formattedTime);
            }
            return formattedTime;
        };

        const maxInterval = monitoringResultsLength === 1 || monitoringResultsLength > 30 ? undefined : 60 * 1000;
        xAxisParameters = { axisLabelFormatter, minInterval: 60 * 1000, maxInterval };

        return xAxisParameters;
    }

    private formatTimeForTotalDataPointsBetween11And15(minutes: number, date: Date, formattedTime: string): string {
        if (minutes % 2 === 0 && date.getSeconds() === 0) {
            return formattedTime;
        }
        return '';
    }

    private formatTimeForTotalDataPointsGreaterThan30(minutes: number, date: Date, formattedTime: string): string {
        if (minutes % 10 === 0 && date.getSeconds() === 0) {
            return formattedTime;
        }
        return '';
    }

    private formatTimeForTotalDataPointsBetween16And30(minutes: number, date: Date, formattedTime: string): string {
        if (minutes % 5 === 0 && date.getSeconds() === 0) {
            return formattedTime;
        }
        return '';
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getLineTooltipFormatter(parameters: any): string {
        const date = new Date(parameters.value[0]);
        const hours = date.getHours();
        const minutes = `0${date.getMinutes()}`;
        const formattedTime = `${hours}:${minutes.slice(-2)}`;

        const value = (parameters.value[1] * 100).toFixed(2);

        return `${formattedTime}, ${value}%`;
    }

    private getPieCustomOptions(usedDisk: number, availableDisk: number, totalDisk: number, theme: DpThemeType): Record<string, unknown> {

        return {
            graphic: [{
                type: MonitoringConstants.TYPE_TEXT, left: MonitoringConstants.LEFT_20, bottom: MonitoringConstants.BOTTOM_30, silent: true, style: {
                    text: this.translateService.instant(`${this.i18nPrefixDisk}.available`),
                    fill: this.getColorText(0, theme),
                    fontSize: 14
                }
            },
            {
                type: MonitoringConstants.TYPE_TEXT, right: MonitoringConstants.RIGHT_20, bottom: MonitoringConstants.BOTTOM_30, silent: true, style: {
                    text: `${availableDisk} GBs`,
                    fill: this.getColorText(1, theme),
                    fontSize: 14
                }
            },
            {
                type: MonitoringConstants.TYPE_TEXT, left: MonitoringConstants.LEFT_20, bottom: MonitoringConstants.BOTTOM_20, silent: true, style: {
                    text: this.translateService.instant(`${this.i18nPrefixDisk}.used`),
                    fill: this.getColorText(2, theme),
                    fontSize: 14
                }
            },
            {
                type: MonitoringConstants.TYPE_TEXT, right: MonitoringConstants.RIGHT_20, bottom: MonitoringConstants.BOTTOM_20, silent: true, style: {
                    text: `${usedDisk} GBs`,
                    fill: this.getColorText(3, theme),
                    fontSize: 14
                }
            },
            {
                type: MonitoringConstants.TYPE_TEXT, left: MonitoringConstants.LEFT_20, bottom: MonitoringConstants.BOTTOM_10, silent: true, style: {
                    text: this.translateService.instant(`${this.i18nPrefixDisk}.total`),
                    fill: this.getColorText(4, theme),
                    fontSize: 14
                }
            },
            {
                type: MonitoringConstants.TYPE_TEXT, right: MonitoringConstants.RIGHT_20, bottom: MonitoringConstants.BOTTOM_10, silent: true, style: {
                    text: `${totalDisk} GBs`,
                    fill: this.getColorText(5, theme),
                    fontSize: 14
                }
            }]
        };
    }

    private getColorText(labelIndex: number, theme: DpThemeType): string {
        if (theme === DpThemeType.Light) {
            switch (labelIndex) {
                case 0:
                    return DpChartColor.Green;
                case 2:
                    return DpChartColor.Red;
                default:
                    return DpChartColor.Black;
            }
        } else {
            switch (labelIndex) {
                case 0:
                    return 'hsl(240, 90%, 40%)';
                case 2:
                    return 'hsl(240, 90%, 80%)';
                default:
                    return DpChartColor.White;
            }
        }

    }

    private roundToNearestEven(value: number): number {
        const rounded = Math.round(value);
        return rounded % 2 === 0 ? rounded : rounded - Math.sign(rounded);
    }
}